import { t, useUiLocale } from '@/i18n/locale';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  buddyProfileSchema,
  memoryInputSchema,
  type BuddyProfile,
  type MemoryInput,
  type BuddyMemory,
} from './companionApi';
import { useCompanion } from './CompanionContext';
import { companionError } from './CompanionSession';
import { BuddyAvatar } from './BuddyAvatar';

export function MemoryDialog({
  text,
  memory,
  onClose,
}: {
  text: string;
  memory?: BuddyMemory;
  onClose: () => void;
}) {
  useUiLocale();
  const session = useCompanion(),
    dialog = useRef<HTMLDialogElement>(null),
    [error, setError] = useState('');
  const form = useForm<MemoryInput>({
    resolver: zodResolver(memoryInputSchema),
    defaultValues: { text: text.slice(0, 100), category: memory?.category || 'interest' },
  });
  useEffect(() => {
    const prior = document.activeElement;
    dialog.current?.showModal();
    return () => {
      if (prior instanceof HTMLElement && prior.isConnected) prior.focus();
    };
  }, []);
  const save = form.handleSubmit(async (value) => {
    if (!session.snapshot?.memory_enabled) return;
    setError('');
    try {
      const next = await session.client.remember(
        session.kidId,
        session.snapshot.revision,
        memory?.id || crypto.randomUUID(),
        value,
        session.audience === 'parent',
      );
      session.update(next, !!memory);
      onClose();
    } catch (caught) {
      setError(companionError(caught));
    }
  });
  return (
    <dialog
      ref={dialog}
      onCancel={onClose}
      className="companion-dialog"
      aria-labelledby="memory-dialog-title"
    >
      <form onSubmit={save}>
        <div className="flex items-center justify-between gap-4">
          <h2 id="memory-dialog-title" className="text-xl font-bold">
            {memory ? t('Edit this memory') : t('What should I remember?')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-pill-ghost"
            aria-label={t('Close memory')}
          >
            ×
          </button>
        </div>
        <p className="my-4 text-sm leading-relaxed text-slate2">
          {' '}
          {t('Keep one small thing you choose. You can edit or forget it later.')}{' '}
        </p>
        <label className="block text-sm font-semibold">
          {' '}
          {t('One thing to remember')}{' '}
          <textarea
            {...form.register('text')}
            maxLength={100}
            rows={3}
            className="input mt-2 w-full"
            autoFocus
          />
        </label>
        {form.formState.errors.text && <p role="alert">{t('Write up to 100 characters.')}</p>}
        <label className="mt-4 block text-sm font-semibold">
          {' '}
          {t('Category')}{' '}
          <select {...form.register('category')} className="input mt-2 w-full">
            <option value="interest">{t('Interest')}</option>
            <option value="preference">{t('Preference')}</option>
            <option value="goal">{t('Something to try')}</option>
          </select>
        </label>
        {error && (
          <p role="alert" className="my-3 text-sm text-danger-600">
            {t(error)}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn-pill-ghost" onClick={onClose}>
            {' '}
            {t('Not now')}{' '}
          </button>
          <button
            className="btn-pill-primary"
            disabled={form.formState.isSubmitting || !session.snapshot?.memory_enabled}
          >
            {form.formState.isSubmitting ? t('Saving…') : t('Confirm memory')}
          </button>
        </div>
      </form>
    </dialog>
  );
}
export function ProfileDialog({ onClose }: { onClose: () => void }) {
  useUiLocale();
  const session = useCompanion(),
    dialog = useRef<HTMLDialogElement>(null),
    [error, setError] = useState('');
  const form = useForm<BuddyProfile>({
    resolver: zodResolver(buddyProfileSchema),
    defaultValues: session.profile,
  });
  const draft = form.watch();
  useEffect(() => {
    const prior = document.activeElement;
    dialog.current?.showModal();
    return () => {
      if (prior instanceof HTMLElement && prior.isConnected) prior.focus();
    };
  }, []);
  const save = form.handleSubmit(async (profile) => {
    if (!session.snapshot) return;
    try {
      session.update(
        await session.client.profile(session.kidId, session.snapshot.revision, profile),
      );
      onClose();
    } catch (caught) {
      setError(companionError(caught));
    }
  });
  return (
    <dialog
      ref={dialog}
      onCancel={onClose}
      className="companion-dialog max-w-2xl"
      aria-labelledby="profile-dialog-title"
    >
      <form onSubmit={save}>
        <div className="flex items-center justify-between">
          <h2 id="profile-dialog-title" className="text-xl font-bold">
            {' '}
            {t('Make your buddy yours')}{' '}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Close wardrobe')}
            className="btn-pill-ghost"
          >
            ×
          </button>
        </div>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          <BuddyAvatar profile={draft} className="h-64 rounded-2xl bg-wash-sky" />
          <div className="space-y-3">
            <label className="block text-sm">
              {' '}
              {t('Buddy name')}{' '}
              <input {...form.register('name')} maxLength={12} className="input mt-1 w-full" />
            </label>
            <label className="block text-sm">
              {' '}
              {t('Character')}{' '}
              <select {...form.register('character')} className="input mt-1 w-full">
                <option value="tuantuan">{t('Tuan Tuan')}</option>
                <option value="plush">{t('Rong Rong')}</option>
              </select>
            </label>
            <label className="block text-sm">
              {' '}
              {t('Hat')}{' '}
              <select {...form.register('hat')} className="input mt-1 w-full">
                <option value="none">{t('No hat')}</option>
                <option value="cap">{t('Explorer')}</option>
                <option value="beanie">{t('Cosy beanie')}</option>
                <option value="crown">{t('Little crown')}</option>
                <option value="beret">{t('Flower beret')}</option>
                <option value="sailor">{t('Sailor')}</option>
                <option value="party">{t('Party star')}</option>
              </select>
            </label>
            <label className="block text-sm">
              {' '}
              {t('Ears')}{' '}
              <select
                {...form.register('earStyle')}
                disabled={draft.character === 'tuantuan'}
                className="input mt-1 w-full"
              >
                <option value="up">{t('Both up')}</option>
                <option value="asymmetric">{t('One soft ear')}</option>
                <option value="down">{t('Both soft')}</option>
              </select>
            </label>
            <label className="block text-sm">
              {' '}
              {t('Room light')}{' '}
              <select {...form.register('room')} className="input mt-1 w-full">
                <option value="cream">{t('Warm daylight')}</option>
                <option value="sky">{t('Blue sky')}</option>
                <option value="mint">{t('Mint glow')}</option>
              </select>
            </label>
          </div>
        </div>
        <fieldset className="mt-5">
          <legend className="text-sm font-semibold">{t('Little accessories')}</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            {(
              [
                'satchel',
                'scarf',
                'backpack',
                'bow',
                'pendant',
                'moonbag',
              ] as const
            )
              .filter((x) => draft.character !== 'tuantuan' || x !== 'scarf')
              .map((value) => (
                <label key={value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    value={t(value)}
                    checked={draft.accessories.includes(value)}
                    onChange={(event) => {
                      const peers = ['satchel', 'moonbag'].includes(value)
                        ? ['satchel', 'moonbag']
                        : ['scarf', 'bow', 'pendant'].includes(value)
                          ? ['scarf', 'bow', 'pendant']
                          : [];
                      const selected = form
                        .getValues('accessories')
                        .filter(
                          (item) =>
                            item !== value && (!event.target.checked || !peers.includes(item)),
                        );
                      form.setValue(
                        'accessories',
                        event.target.checked ? [...selected, value] : selected,
                        { shouldValidate: true },
                      );
                    }}
                  />
                  {t(value)}
                </label>
              ))}
          </div>
        </fieldset>
        {error && (
          <p className="my-3 text-sm text-danger-600" role="alert">
            {t(error)}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn-pill-ghost" onClick={onClose}>
            {' '}
            {t('Cancel')}{' '}
          </button>
          <button
            className="btn-pill-primary"
            disabled={
              form.formState.isSubmitting || (!form.formState.isValid && form.formState.isSubmitted)
            }
          >
            {' '}
            {t('Save my buddy')}{' '}
          </button>
        </div>
      </form>
    </dialog>
  );
}
